// Bildschirm "Rollenkatalog" (ADR-0067): die Rollen als Zeilen sehen, eine
// Rolle vorschlagen, freigeben, umbenennen, beenden und Vorschlaege verwerfen.
//
// EINE HAUPTAKTION (5.2 Nr. 2): "Rolle vorschlagen". Alles andere sind
// Zeilenaktionen; jede oeffnet erst eine benannte Rueckfrage direkt unter
// ihrer Zeile, ueber die volle Breite. Freigeben verlangt den Pruefsatz (F3, Art. 9 DSGVO), Beenden steht
// im Menue der Zeile, damit es nicht neben "Umbenennen" verwechselt wird.
//
// WELCHE KNOEPFE: entscheidet der Server je Zeile (canApprove/canRename/
// canEnd). Fehlt "Freigeben", obwohl etwas wartet, sagt eine Zweitzeile
// warum (vier Augen).
//
// ZEILENRASTER WIE IM STICHWORTKATALOG (Restliste B3, Dirigent 21.10.): Name
// und Pille, Beschreibung, die Zahl als Satz; rechts die Aktionen, schmal
// darunter. Was auf die Lesende wartet, steht oben.
//
// BEENDETE ROLLEN stehen zugeklappt unter der Liste, ohne Aktionen.
//
// EIN ERKLAERSATZ: was "fuer alle Haeuser" heisst und was die Zahl zaehlt,
// steht einmal oben.
//
// Nach jedem Befehl laedt der Katalog neu, und der Fokus geht in die
// Statusregion (Muster Stichwortkatalog).

import { Fragment, useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Field,
  Input,
  makeStyles,
  mergeClasses,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle2,
  Textarea,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { keepsRequest, requestFor, type HeldRequest } from '../client-request.ts';
import {
  appendPage,
  appliedSentence,
  appliedSentenceS2,
  approvalNote,
  approveRoleType,
  DISCARD_EFFECT,
  END_EFFECT,
  endRoleType,
  pendingRenameNote,
  renamedSentence,
  renamePending,
  renameRoleType,
  SAME_NAME_FIELD,
  CATALOG_NOTE,
  needsStewardNote,
  STEWARD_CHANGE_NOTE,
  fetchRoleTypes,
  MAXIMUM_ROLE_DESCRIPTION_LENGTH,
  MAXIMUM_ROLE_NAME_LENGTH,
  proposeRoleType,
  specialCategorySentence,
  STATUS_WORT,
  assignmentSentence,
  CHANGE_UNCERTAIN_MESSAGE,
  LOAD_UNREACHABLE_MESSAGE,
  MORE_UNREACHABLE_MESSAGE,
  PROPOSAL_UNCERTAIN_MESSAGE,
  type RoleTypeCommandResult,
  type RoleTypeStatus,
  type RoleTypeView,
} from '../context-role-types.ts';
import { MehrKnopf } from '../shell/mehr-knopf.tsx';
import { usePflegeStyles } from '../shell/pflegeabschnitt.tsx';
import { meldungsIntent } from '../shell/use-section-commands.ts';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { VorgangNach } from '../shell/zustand.tsx';
import { AnlegenFormular, AnlegenOeffner, useAnlegen } from './pflege-kopf.tsx';
import { beiEscapeSchliessen } from './kontakt/plus-symbol.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';
import { useZeilenEntwuerfe } from './zeilen-entwurf.ts';

const useStyles = makeStyles({
  // Erklaersatz links, "Rolle vorschlagen" rechts; schmal untereinander.
  kopf: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem 1rem' },
  satz: { flex: '1 1 20rem', minWidth: 0, color: tokens.colorNeutralForeground2 },
  formular: { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem', maxWidth: '40rem' },
  // Eine leere Statusregion bleibt im Baum (sonst ginge die naechste Ansage verloren), traegt aber keinen Abstand.
  hinweis: { display: 'block', marginTop: '0.75rem', ':empty': { marginTop: 0 } },
  abschnitt: { marginTop: '1rem' },
  liste: { listStyle: 'none', margin: 0, padding: 0 },
  // Zeile wie im Stichwortkatalog: links Name, Pille und Saetze, rechts die Zeilenknoepfe;
  // schmal stehen die Knoepfe darunter, die Rueckfrage immer ueber die volle Breite.
  eintrag: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '0.5rem 0.875rem',
    padding: '0.75rem 0',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowWrap: 'anywhere',
  },
  text: { flex: '1 1 20rem', minWidth: 0 },
  voll: { flex: '1 1 100%' },
  namenszeile: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.5rem' },
  beendet: { color: tokens.colorNeutralForeground2 },
  zeilensatz: { display: 'block', marginTop: '0.25rem' },
  zweitzeile: { display: 'block', color: tokens.colorNeutralForeground2 },
  fuss: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.75rem', paddingTop: '0.75rem', borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  // Eine Pille bricht nie um: zweizeilig ueberlagert sich der Text in der festen Hoehe (DESIGN #587).
  pille: { whiteSpace: 'nowrap' },
  rueckfrage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    maxWidth: '40rem',
    // Bis 40rem so breit wie die Zeile: sonst schrumpfte die Rueckfrage aufs Feld in Fluents Standardbreite.
    width: '100%',
    boxSizing: 'border-box',
  },
  // Felder fuer Namen bis 80 und Beschreibungen bis 500 Zeichen: volle Breite, hoechstens 40rem (Rundgang 67).
  feld: { alignSelf: 'stretch', width: '100%', maxWidth: '40rem' },
  eingabe: { width: '100%' },
  // Drei Zeilen, waechst mit dem Text bis acht; danach scrollt das Feld.
  beschreibung: { width: '100%', '& textarea': { minHeight: '4.5rem', maxHeight: '12rem', fieldSizing: 'content' } },
  knoepfe: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  aktionen: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' },
  mehr: { minWidth: '32px', minHeight: '32px' },
});

/** Fremde Vorschlaege zuerst, dann eigene, dann der Rest (DESIGN zu B3). */
function zeilenRang(rolle: RoleTypeView): number {
  if (rolle.typeStatus !== 'proposed') return 2;
  return rolle.proposedByMe ? 1 : 0;
}

const PILLE: Readonly<Record<RoleTypeStatus, 'success' | 'warning' | 'subtle'>> = {
  proposed: 'warning',
  active: 'success',
  ended: 'subtle',
};

const FELDSAETZE: Readonly<Record<string, string>> = Object.freeze({
  field_required: 'Bitte ausfüllen.',
  field_value_unsupported: 'Diesen Wert gibt es hier nicht.',
  field_malformed: 'Erlaubt sind Buchstaben, Ziffern, Leerzeichen, Doppelpunkt, Punkt, Apostroph, Schrägstrich, Und-Zeichen und Bindestrich; am Anfang ein Buchstabe oder eine Ziffer.',
});

/**
 * Die Grenze in Zeichen wie der Server, nicht per maxLength: das zaehlt UTF-16-Einheiten und
 * stoppte bei Emoji schon bei der Haelfte, waehrend der Zaehler noch Platz nennt (wie zur-sammlung.tsx).
 */
function hoechstens(wert: string, grenze: number): string {
  const zeichen = [...wert];
  return zeichen.length > grenze ? zeichen.slice(0, grenze).join('') : wert;
}

/** "12 von 500 Zeichen", gezaehlt wie der Server des Rollenkatalogs (Zeichen, nicht UTF-16-Einheiten). */
function zeichenSatz(wert: string, grenze: number): string {
  return `${[...wert].length.toLocaleString('de-DE')} von ${grenze.toLocaleString('de-DE')} Zeichen`;
}

/**
 * Der Satz am Feld; „zu lang“ nennt Grenze und Ist-Wert, damit man sieht, wie viel weg muss.
 * `wert` ist, was gesendet wurde; gezaehlt werden Zeichen, nicht UTF-16-Einheiten.
 */
function feldSatz(feld: string, reasonCode: string | undefined, wert: string): string {
  if (reasonCode === 'field_too_long') {
    const grenze = feld === 'description' ? MAXIMUM_ROLE_DESCRIPTION_LENGTH : MAXIMUM_ROLE_NAME_LENGTH;
    return `Höchstens ${grenze.toLocaleString('de-DE')} Zeichen. Jetzt sind es ${Array.from(wert).length.toLocaleString('de-DE')}.`;
  }
  return FELDSAETZE[reasonCode ?? ''] ?? 'Bitte prüfen.';
}

export const STATUS_REGION_ID = 'rollenkatalog-status';
const feldId = (feld: string): string => `rolle-${feld}`;
const NAME_TAKEN_FIELD = 'Eine Rolle mit diesem Namen gibt es schon.';
/** Der Ausloeser einer Rueckfrage (data-rueckfrage="<modus>:<code>"); nach Abbrechen geht der Fokus zurueck. */
function sichtbarerAusloeser(modus: Modus, roleCode: string): HTMLElement | undefined {
  return [...document.querySelectorAll<HTMLElement>(`[data-rueckfrage="${CSS.escape(`${modus}:${roleCode}`)}"]`)].find((knopf) => knopf.offsetParent !== null);
}

type Modus = 'freigeben' | 'umbenennen' | 'beenden' | 'verwerfen';

/** Erlaubt der Server diese Rueckfrage an dieser Zeile noch? Verwerfen und Zurueckziehen laufen ueber Beenden. */
function darf(rolle: RoleTypeView, modus: Modus): boolean {
  if (modus === 'freigeben') return rolle.canApprove;
  if (modus === 'umbenennen') return rolle.canRename;
  return rolle.canEnd;
}

/** Die eine offene Rueckfrage an ihrer Zeile (es gibt nie zwei). */
interface Rueckfrage {
  readonly roleCode: string;
  /** Der Stand der Rolle beim Oeffnen; aendert er sich beim Neuladen, gilt die Rueckfrage nicht mehr. */
  readonly lockVersion: number;
  /** Der Name beim Oeffnen; der Satz zum Schliessen nennt ihn auch, wenn die Rolle fehlt. */
  readonly name: string;
  readonly modus: Modus;
  readonly geprueft: boolean;
  readonly neuerName: string;
  /** Satz am Feld "Neuer Name" (leer, gleich, belegt oder vom Server abgelehnt). */
  readonly feldfehler?: string | undefined;
}

/** Offen ist nur, was gespeichert sein kann; jede Antwort von LIAISON und jedes gescheiterte Laden ist ein Fehler. */
const OFFENE_MELDUNGEN: ReadonlySet<PlainMessage> = new Set([PROPOSAL_UNCERTAIN_MESSAGE, CHANGE_UNCERTAIN_MESSAGE]);

function Meldung({ message }: { readonly message: PlainMessage }): ReactElement {
  return (
    <MessageBar intent={meldungsIntent(message, OFFENE_MELDUNGEN.has(message))}>
      <MessageBarBody>
        <MessageBarTitle>{message.headline}</MessageBarTitle>
        <div>{message.explanation}</div>
        <div>{message.nextStep}</div>
      </MessageBarBody>
    </MessageBar>
  );
}

export interface RollenkatalogScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

export function RollenkatalogScreen({ apiBaseUrl, accessToken, onReauthenticate }: RollenkatalogScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const { zeilenknopf } = usePflegeStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const [rollen, setRollen] = useState<readonly RoleTypeView[]>([]);
  /** Eine aktive Rolle ohne Pflegeknopf: die Seite nennt das Recht "Datenpflege" (Kopf und eigener Vorschlag). */
  const ohneRecht = needsStewardNote(rollen);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [weitereLaedt, setWeitereLaedt] = useState(false);
  /** Was "Weitere laden" gebracht hat; die Region im Tabellenfuss sagt es an. */
  const [nachgeladen, setNachgeladen] = useState<{ readonly satz: string; readonly mal: number } | undefined>(undefined);
  const nachgeladenRef = useRef<HTMLSpanElement | null>(null);
  const [ladefehler, setLadefehler] = useState<PlainMessage | undefined>(undefined);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  /** Warum eine offene Rueckfrage beim Neuladen schloss; sonst verschwaende sie ohne Grund unter dem Finger. */
  const [geschlossen, setGeschlossen] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [eingabe, setEingabe] = useState({ name: '', description: '' });
  const [feldfehler, setFeldfehler] = useState<Readonly<Record<string, string>>>({});
  const [gehalten, setGehalten] = useState<HeldRequest | undefined>(undefined);
  const anlegen = useAnlegen(false, 'rollenkatalog');
  /** Die offene Rueckfrage: Freigeben, Umbenennen, Beenden oder Verwerfen einer Rolle. */
  const [offen, setOffen] = useState<Rueckfrage | undefined>(undefined);
  /** Ein getippter neuer Name bleibt je Zeile, auch wenn dazwischen eine andere Zeile offen war. */
  const entwuerfe = useZeilenEntwuerfe();
  const oeffnen = (rolle: RoleTypeView, modus: Modus): void =>
    setOffen({ roleCode: rolle.roleCode, lockVersion: rolle.lockVersion, name: rolle.name, modus, geprueft: false, neuerName: entwuerfe.lesen(rolle.roleCode, rolle.name) });
  const statusRef = useRef<HTMLDivElement | null>(null);
  const rueckfrageRef = useRef<HTMLDivElement | null>(null);
  /** Die offene Rueckfrage fuer `load`, das sonst mit dem Stand seines Aufrufs arbeitete. */
  const offenRef = useRef(offen);
  offenRef.current = offen;
  const [fokus, setFokus] = useState(0);
  /**
   * Betrifft ein Fehler ein Feld (leer, ungueltig, Name schon vergeben), geht der Fokus
   * in dieses Feld statt in die Statusregion: dort wird korrigiert (DESIGN-P3 #587).
   * Die Meldung steht am Feld (aria-invalid, Beschreibung) und weiter in der Statusregion.
   */
  const [feldFokus, setFeldFokus] = useState<{ readonly feld: string; readonly mal: number } | undefined>(undefined);
  /** Der Ausloeser, an den der Fokus nach dem Abbrechen zurueckgeht. */
  const [zurueckZu, setZurueckZu] = useState<{ readonly modus: Modus; readonly roleCode: string } | undefined>(undefined);
  const laufRef = useRef(0);

  useEffect(() => {
    if (fokus > 0) statusRef.current?.focus();
  }, [fokus]);
  // War es die letzte Seite, verschwindet "Weitere laden"; der Fokus faellt dann nicht auf
  // BODY, sondern auf die Ansage im Fuss. Sonst bleibt er auf dem Knopf.
  useEffect(() => {
    if (nachgeladen !== undefined && cursor === undefined && document.activeElement === document.body) nachgeladenRef.current?.focus();
  }, [nachgeladen, cursor]);
  useEffect(() => {
    if (feldFokus !== undefined) document.getElementById(feldId(feldFokus.feld))?.focus();
  }, [feldFokus]);

  // Die Rueckfrage ersetzt die Knoepfe ihrer Zeile; ohne Fokusfuehrung fiele der
  // Fokus mit dem Knopf auf BODY (WCAG 2.4.3). Beim Oeffnen geht er ins
  // Kaestchen bzw. Feld und die Gruppe ins Bild, beim Abbrechen zurueck auf den
  // Knopf der Zeile. Es gibt immer nur eine: eine neue ersetzt die alte.
  const offeneRueckfrage = offen === undefined ? undefined : `${offen.modus}:${offen.roleCode}`;
  useEffect(() => {
    if (offeneRueckfrage === undefined) return;
    rueckfrageRef.current?.scrollIntoView({ block: 'nearest' });
    // Freigeben und Umbenennen: ins Kaestchen bzw. Feld. Beenden und Verwerfen haben kein
    // Eingabeelement; dort geht der Fokus auf die Gruppe selbst, die Name und Wirkung vorliest,
    // statt auf den Bestaetigungsknopf (ein zweites Enter bestaetigte sonst ungelesen).
    const erstes = rueckfrageRef.current?.querySelector<HTMLElement>('input[type="checkbox"], input[type="text"], input:not([type])');
    (erstes ?? rueckfrageRef.current)?.focus();
  }, [offeneRueckfrage]);
  useEffect(() => {
    if (zurueckZu === undefined) return;
    // Ist der Knopf nicht mehr da (die Zeile fiel beim Neuladen heraus), nie BODY: die Statusregion.
    (sichtbarerAusloeser(zurueckZu.modus, zurueckZu.roleCode) ?? statusRef.current)?.focus();
    setZurueckZu(undefined);
  }, [zurueckZu]);
  const abbrechen = (): void => {
    if (offen === undefined) return;
    // Wer abbricht, verwirft bewusst; beim naechsten Oeffnen steht wieder der heutige Name.
    entwuerfe.vergessen(offen.roleCode);
    setZurueckZu({ modus: offen.modus, roleCode: offen.roleCode });
    setOffen(undefined);
  };

  /** Laedt die erste Seite; `true`, wenn die Liste jetzt dem Server entspricht. */
  const load = useCallback(async (): Promise<boolean> => {
    const meinLauf = ++laufRef.current;
    setStatus((alt) => (alt === 'geladen' ? alt : 'laedt'));
    try {
      const token = await accessToken();
      if (meinLauf !== laufRef.current) return false;
      if (token === undefined) {
        onReauthenticate();
        return false;
      }
      const result = await fetchRoleTypes({ baseUrl: apiBaseUrl, accessToken: token });
      if (meinLauf !== laufRef.current) return false;
      if (result.outcome === 'rejected') {
        setLadefehler(result.message);
        // Ein schon geladener Stand bleibt stehen; nur der erste Abruf endet ohne Tabelle.
        setStatus((alt) => (alt === 'geladen' ? alt : 'abgelehnt'));
        return false;
      }
      setLadefehler(undefined);
      // Die offene Rueckfrage gilt nur fuer den Stand, auf dem sie geoeffnet wurde. Fehlt die Rolle
      // (nur die erste Seite wird neu geladen), ist sie beendet oder hat sie einen neuen Stand
      // (jemand anderes hat umbenannt), schliesst die Rueckfrage: offen bliebe sie unsichtbar oder
      // saendete einen alten Entwurf gegen den neuen Stand, ohne dass ein Konflikt kaeme.
      const alt = offenRef.current;
      const jetzt = alt === undefined ? undefined : result.page.roleTypes.find((rolle) => rolle.roleCode === alt.roleCode);
      if (alt !== undefined && (jetzt === undefined || jetzt.typeStatus === 'ended' || jetzt.lockVersion !== alt.lockVersion || !darf(jetzt, alt.modus))) {
        // Lag der Fokus in der Rueckfrage, verschwaende er mit ihr: er geht in die Statusregion.
        const fokusDarin = rueckfrageRef.current?.contains(document.activeElement) === true;
        setOffen(undefined);
        setGeschlossen(
          jetzt === undefined
            ? `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil die Rolle nicht mehr in der geladenen Liste steht.`
            : jetzt.typeStatus === 'ended'
              ? `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil die Rolle inzwischen beendet ist.`
              : jetzt.lockVersion === alt.lockVersion
                ? `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil du die Rolle nicht mehr ändern darfst.`
                : `Die Rückfrage zu „${alt.name}“ ist geschlossen, weil die Rolle inzwischen geändert wurde. Öffne sie erneut, wenn es dabei bleibt.`,
        );
        if (fokusDarin) setFokus((zahl) => zahl + 1);
      }
      setRollen(result.page.roleTypes);
      setCursor(result.page.nextCursor);
      setStatus('geladen');
      return true;
    } catch {
      if (meinLauf !== laufRef.current) return false;
      setLadefehler(LOAD_UNREACHABLE_MESSAGE);
      setStatus((alt) => (alt === 'geladen' ? alt : 'abgelehnt'));
      return false;
    }
  }, [accessToken, apiBaseUrl, onReauthenticate]);

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  const weitereLaden = async (): Promise<void> => {
    if (cursor === undefined || weitereLaedt) return;
    const meinLauf = laufRef.current;
    setWeitereLaedt(true);
    setFehler(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchRoleTypes({ baseUrl: apiBaseUrl, accessToken: token, cursor });
      // Ein Neuladen dazwischen hat die Liste ersetzt; diese Seite gehoert nicht mehr dazu.
      if (meinLauf !== laufRef.current) return;
      if (result.outcome === 'rejected') {
        setFehler(result.message);
        setFokus((zahl) => zahl + 1);
        return;
      }
      const bekannt = new Set(rollen.map((rolle) => rolle.roleCode));
      const neu = result.page.roleTypes.filter((rolle) => !bekannt.has(rolle.roleCode)).length;
      setRollen((alt) => appendPage(alt, result.page.roleTypes));
      setNachgeladen((alt) => ({
        satz: neu === 0 ? 'Keine weiteren Rollen.' : neu === 1 ? '1 weitere Rolle geladen.' : `${neu.toLocaleString('de-DE')} weitere Rollen geladen.`,
        mal: (alt?.mal ?? 0) + 1,
      }));
      setCursor(result.page.nextCursor);
    } catch {
      if (meinLauf !== laufRef.current) return;
      setFehler(MORE_UNREACHABLE_MESSAGE);
      setFokus((zahl) => zahl + 1);
    } finally {
      setWeitereLaedt(false);
    }
  };

  /** Fuehrt einen Befehl aus, meldet das Ergebnis und laedt neu. */
  /**
   * `felder` sagt, wohin Feldfehler gehoeren: ins Vorschlagsformular oder ans Feld "Neuer Name"
   * der offenen Rueckfrage (die dann stehen bleibt); ohne Angabe gibt es keine Felder.
   */
  const ausfuehren = async (
    run: (token: string) => Promise<RoleTypeCommandResult>,
    erfolg: string,
    felder?: 'vorschlag' | 'umbenennen',
    /** Was in den Feldern gesendet wurde; der Satz „zu lang“ nennt die Zahl. */
    gesendet?: { readonly name: string; readonly description?: string },
  ): Promise<RoleTypeCommandResult | undefined> => {
    setBusy(true);
    setFehler(undefined);
    setBestaetigung(undefined);
    setGeschlossen(undefined);
    let result: RoleTypeCommandResult | undefined;
    let feld: string | undefined;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return undefined;
      }
      result = await run(token);
      const nameFehler =
        result.outcome === 'conflict' && (result.reasonCode === 'context_role_type_name_taken' || result.reasonCode === 'context_role_type_name_unchanged')
          ? result.reasonCode
          : undefined;
      if (result.outcome === 'applied') {
        setBestaetigung(erfolg);
      } else if (felder === 'umbenennen' && (result.outcome === 'invalid' || nameFehler !== undefined)) {
        // Die Rueckfrage bleibt offen; die Meldung steht am Feld, dort wird korrigiert.
        const satz =
          result.outcome === 'invalid'
            ? feldSatz('name', result.errors.find((e) => e.fieldPath === 'name')?.reasonCode, gesendet?.name ?? '')
            : nameFehler === 'context_role_type_name_unchanged'
              ? SAME_NAME_FIELD
              : NAME_TAKEN_FIELD;
        setOffen((alt) => (alt === undefined ? alt : { ...alt, feldfehler: satz }));
        feld = 'neuer-name';
      } else if (result.outcome === 'invalid') {
        setFeldfehler(Object.fromEntries(result.errors.map((e) => [e.fieldPath, feldSatz(e.fieldPath, e.reasonCode, (e.fieldPath === 'description' ? gesendet?.description : gesendet?.name) ?? '')])));
        feld = result.errors.map((e) => e.fieldPath).find((pfad) => pfad === 'name' || pfad === 'description');
        setFehler({
          headline: 'Bitte prüfe die markierten Felder.',
          explanation: 'Es wurde nichts gespeichert.',
          nextStep: 'Korrigiere die Eingabe und versuche es erneut.',
          retryable: false,
        });
      } else if (felder === 'vorschlag' && nameFehler === 'context_role_type_name_taken') {
        setFeldfehler({ name: NAME_TAKEN_FIELD });
        feld = 'name';
      }
      // Offen bleibt die Umbenennen-Rueckfrage nur fuer einen Satz am Feld. Nach jedem anderen
      // Ausgang schliesst sie: sonst sendete ein zweiter Klick mit dem frisch geladenen Stand,
      // ohne dass die Person die fremde Aenderung gesehen hat.
      if (felder === 'umbenennen' && feld !== 'neuer-name') setOffen(undefined);
      // Ein Satz am Feld "Neuer Name" hat am Server nichts geaendert. Neu laden hiesse hier: nur
      // die erste Seite -- eine Rolle von einer nachgeladenen Seite fiele samt Rueckfrage und
      // Meldung heraus.
      const frisch = feld === 'neuer-name' ? true : await load();
      if (result.outcome !== 'applied' && result.outcome !== 'invalid' && !(felder === 'umbenennen' && nameFehler !== undefined)) {
        // Die Saetze sagen "Die Liste ist jetzt aktuell"; das stimmt nur, wenn das Neuladen gelang.
        setFehler(frisch ? result.message : { ...result.message, nextStep: 'Lade den Rollenkatalog neu und prüfe dann den aktuellen Stand.', retryable: true });
      }
    } catch {
      if (felder === 'umbenennen') setOffen(undefined);
      setFehler(felder === 'vorschlag' ? PROPOSAL_UNCERTAIN_MESSAGE : CHANGE_UNCERTAIN_MESSAGE);
    } finally {
      setBusy(false);
      const ziel = feld;
      if (ziel !== undefined) setFeldFokus((alt) => ({ feld: ziel, mal: (alt?.mal ?? 0) + 1 }));
      else setFokus((zahl) => zahl + 1);
    }
    return result;
  };

  const vorschlagen = async (): Promise<void> => {
    const name = eingabe.name.trim();
    if (name === '') {
      // Am Feld derselbe Satz wie in der Statusregion: ein Fehler, ein Satz.
      setFeldfehler({ name: 'Bitte gib der Rolle einen Namen.' });
      setBestaetigung(undefined);
      setGeschlossen(undefined);
      setFehler({
        headline: 'Bitte gib der Rolle einen Namen.',
        explanation: 'Ohne Namen lässt sich keine Rolle vorschlagen. Es wurde nichts gespeichert.',
        nextStep: 'Ergänze den Namen, zum Beispiel „Übersetzer:in“.',
        retryable: false,
      });
      setFeldFokus((alt) => ({ feld: 'name', mal: (alt?.mal ?? 0) + 1 }));
      return;
    }
    setFeldfehler({});
    const halten = requestFor(gehalten, { name, description: eingabe.description.trim() });
    setGehalten(halten);
    let result: RoleTypeCommandResult | undefined;
    try {
      result = await ausfuehren(
        (token) =>
          proposeRoleType({ baseUrl: apiBaseUrl, accessToken: token, input: { name, description: eingabe.description.trim() }, clientRequestId: halten.id }),
        appliedSentence('propose', name),
        'vorschlag',
        { name, description: eingabe.description.trim() },
      );
    } finally {
      // Die Kennung bleibt nur, wenn eine Wiederholung etwas aendern kann.
      if (result !== undefined && !keepsRequest(result)) setGehalten(undefined);
    }
    // Auch "inzwischen bearbeitet" heisst: der Vorschlag ist angekommen -- das Formular gilt als erledigt.
    if (result?.outcome === 'applied' || (result?.outcome === 'conflict' && result.reasonCode === 'client_request_superseded')) {
      setEingabe({ name: '', description: '' });
      anlegen.erledigt();
    }
  };

  const freigeben = (rolle: RoleTypeView): void => {
    setOffen(undefined);
    void ausfuehren(
      (token) => approveRoleType({ baseUrl: apiBaseUrl, accessToken: token, roleType: rolle, confirmsNoSpecialCategory: true }),
      appliedSentence('approve', rolle.name),
    );
  };

  /** Leer oder unveraendert wird nicht gesendet; der Satz steht am Feld (DESIGN W4). */
  const umbenennen = async (rolle: RoleTypeView, neuerName: string): Promise<void> => {
    const name = neuerName.trim();
    const vorab = name === '' ? 'Bitte gib der Rolle einen Namen.' : name === rolle.name ? SAME_NAME_FIELD : undefined;
    if (vorab !== undefined) {
      setOffen((alt) => (alt === undefined ? alt : { ...alt, feldfehler: vorab }));
      setFeldFokus((alt) => ({ feld: 'neuer-name', mal: (alt?.mal ?? 0) + 1 }));
      return;
    }
    const result = await ausfuehren((token) => renameRoleType({ baseUrl: apiBaseUrl, accessToken: token, roleType: rolle, name }), renamedSentence(rolle, name), 'umbenennen', { name });
    // Gespeichert ist der Entwurf erledigt; nach jedem anderen Ausgang bleibt er fuer den naechsten Versuch.
    if (result?.outcome === 'applied') entwuerfe.vergessen(rolle.roleCode);
  };

  /** Beendet eine vergebbare Rolle oder verwirft bzw. zieht einen Vorschlag zurueck; den Grund setzt endRoleType. */
  const beenden = (rolle: RoleTypeView): void => {
    setOffen(undefined);
    const vorschlag = rolle.typeStatus === 'proposed';
    void ausfuehren(
      (token) => endRoleType({ baseUrl: apiBaseUrl, accessToken: token, roleType: rolle }),
      appliedSentenceS2(vorschlag ? (rolle.proposedByMe ? 'withdraw' : 'discard') : 'end', rolle.name),
    );
  };

  /** "Zurückziehen" am eigenen Vorschlag, sonst "Verwerfen". */
  const verwerfenWort = (rolle: RoleTypeView): string => (rolle.proposedByMe ? 'Zurückziehen' : 'Verwerfen');

  const pille = (rolle: RoleTypeView): ReactElement => {
    // Ein offener neuer Name wartet wie ein Vorschlag auf Freigabe (E7').
    const wartet = renamePending(rolle);
    // Wer freigeben darf, liest wie im Stichwortkatalog, dass es auf sie wartet (DESIGN zu B3).
    const wort = (wartet || rolle.typeStatus === 'proposed') && rolle.canApprove ? 'Wartet auf deine Freigabe' : wartet ? STATUS_WORT.proposed : STATUS_WORT[rolle.typeStatus];
    return (
      <Badge appearance="tint" color={wartet ? 'warning' : PILLE[rolle.typeStatus]} className={own.pille}>
        {wort}
      </Badge>
    );
  };
  // Welche Knoepfe es gibt, entscheidet der Server (canApprove/canRename/canEnd); das Web raet nicht.
  const offenHier = (rolle: RoleTypeView): boolean => offen?.roleCode === rolle.roleCode;
  const zeilenKnopf = (rolle: RoleTypeView, modus: Modus, wort: string): ReactElement => (
    <Button
      data-rueckfrage={`${modus}:${rolle.roleCode}`}
      appearance="secondary"
      size="small"
      className={zeilenknopf}
      disabled={busy}
      onClick={() => oeffnen(rolle, modus)}
      aria-label={`${wort}: ${rolle.name}`}
    >
      {wort}
    </Button>
  );
  /** Die Aktionen einer Zeile: Freigeben, Umbenennen, Verwerfen bzw. Zurueckziehen, und Beenden im "…" (DESIGN W1). */
  const aktionen = (rolle: RoleTypeView): ReactElement | undefined => {
    if (offenHier(rolle) || rolle.typeStatus === 'ended') return undefined;
    const vorschlag = rolle.typeStatus === 'proposed';
    const knoepfe = [
      rolle.canApprove ? <Fragment key="f">{zeilenKnopf(rolle, 'freigeben', 'Freigeben')}</Fragment> : undefined,
      rolle.canRename ? <Fragment key="u">{zeilenKnopf(rolle, 'umbenennen', 'Umbenennen')}</Fragment> : undefined,
      vorschlag && rolle.canEnd ? <Fragment key="v">{zeilenKnopf(rolle, 'verwerfen', verwerfenWort(rolle))}</Fragment> : undefined,
      !vorschlag && rolle.canEnd ? (
        <Menu key="m">
          <MenuTrigger disableButtonEnhancement>
            <MehrKnopf
              data-rueckfrage={`beenden:${rolle.roleCode}`}
              appearance="secondary"
              size="small"
              className={own.mehr}
              disabled={busy}
              bezug={`Weitere Aktionen: ${rolle.name}`}
              icon={<span aria-hidden="true">…</span>}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem aria-label={`Beenden: ${rolle.name}`} onClick={() => oeffnen(rolle, 'beenden')}>
                Beenden …
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      ) : undefined,
    ].filter((knopf) => knopf !== undefined);
    return knoepfe.length === 0 ? undefined : <div className={own.aktionen}>{knoepfe}</div>;
  };
  /** Die offene Rueckfrage unter ihrer Zeile; an allen anderen Zeilen nichts. */
  const rueckfrageAnDerZeile = (rolle: RoleTypeView): ReactElement | undefined =>
    offen === undefined || offen.roleCode !== rolle.roleCode ? undefined : (
      <div
        className={own.rueckfrage}
        role="group"
        aria-labelledby="rolle-rueckfrage-titel"
        aria-describedby={offen.modus === 'umbenennen' ? undefined : 'rolle-rueckfrage-wirkung'}
        tabIndex={-1}
        ref={rueckfrageRef}
        onKeyDown={(event) => {
          // Waehrend der Server antwortet, bleibt die Rueckfrage stehen -- wie "Abbrechen".
          if (busy) return;
          beiEscapeSchliessen(event, abbrechen);
        }}
      >
        <Subtitle2 as="h2" id="rolle-rueckfrage-titel">
          {offen.modus === 'freigeben'
            ? 'Freigeben'
            : offen.modus === 'umbenennen'
              ? 'Umbenennen'
              : offen.modus === 'beenden'
                ? 'Beenden'
                : verwerfenWort(rolle)}
          : {rolle.name}
        </Subtitle2>
        {offen.modus === 'freigeben' ? (
          <>
            <Checkbox
              checked={offen.geprueft}
              onChange={(_event, data) => setOffen({ ...offen, geprueft: data.checked === true })}
              label={specialCategorySentence(rolle.name)}
            />
            <Caption1 id="rolle-rueckfrage-wirkung">
              {renamePending(rolle)
                ? `Danach heißt die Rolle auch an Kontakten „${rolle.name}“.`
                : `Danach lässt sich „${rolle.name}“ an Kontakten vergeben.`}
            </Caption1>
          </>
        ) : undefined}
        {offen.modus === 'umbenennen' ? (
          <Field
            className={own.feld}
            label="Neuer Name"
            required
            validationMessage={offen.feldfehler ?? null}
            // Am Vorschlag gilt noch kein Name an Kontakten; dort braucht es keinen Hinweis (DESIGN N3).
            hint={
              rolle.approvedName === null
                ? null
                : `Eine andere Person gibt den neuen Namen frei; bis dahin heißt die Rolle an Kontakten „${rolle.approvedName}“.`
            }
          >
            <Input
              className={own.eingabe}
              id={feldId('neuer-name')}
              disabled={busy}
              value={offen.neuerName}
              // Wer korrigiert, sieht die alte Meldung am Feld nicht mehr.
              onChange={(_event, data) => {
                const wert = hoechstens(data.value, MAXIMUM_ROLE_NAME_LENGTH);
                setOffen({ ...offen, neuerName: wert, feldfehler: undefined });
                entwuerfe.merken(rolle.roleCode, wert, rolle.name);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !busy) void umbenennen(rolle, offen.neuerName);
              }}
            />
          </Field>
        ) : undefined}
        {offen.modus === 'beenden' ? (
          <Caption1 id="rolle-rueckfrage-wirkung">{END_EFFECT}</Caption1>
        ) : undefined}
        {offen.modus === 'verwerfen' ? (
          <Caption1 id="rolle-rueckfrage-wirkung">{DISCARD_EFFECT}</Caption1>
        ) : undefined}
        <div className={own.knoepfe}>
          {offen.modus === 'freigeben' ? (
            <Button appearance="secondary" disabled={busy || !offen.geprueft} onClick={() => freigeben(rolle)}>
              Freigeben bestätigen
            </Button>
          ) : undefined}
          {offen.modus === 'umbenennen' ? (
            <Button appearance="secondary" disabled={busy} onClick={() => void umbenennen(rolle, offen.neuerName)}>
              Umbenennen bestätigen
            </Button>
          ) : undefined}
          {offen.modus === 'beenden' ? (
            <Button appearance="secondary" disabled={busy} onClick={() => beenden(rolle)}>
              Beenden bestätigen
            </Button>
          ) : undefined}
          {offen.modus === 'verwerfen' ? (
            <Button appearance="secondary" disabled={busy} onClick={() => beenden(rolle)}>
              {verwerfenWort(rolle)} bestätigen
            </Button>
          ) : undefined}
          <NebenKnopf disabled={busy} onClick={abbrechen}>
            Abbrechen
          </NebenKnopf>
        </div>
      </div>
    );
  const zeile = (rolle: RoleTypeView, mitAktionen: boolean): ReactElement => {
    const rueckfrage = mitAktionen ? rueckfrageAnDerZeile(rolle) : undefined;
    return (
      <li key={rolle.roleCode} className={mergeClasses(own.eintrag, rolle.typeStatus === 'ended' && own.beendet)}>
        <div className={own.text}>
          <div className={own.namenszeile}>
            <Body1>
              <strong>{rolle.name}</strong>
            </Body1>
            {pille(rolle)}
          </div>
          {rolle.description !== null && rolle.description !== '' ? <Body1 className={own.zeilensatz}>{rolle.description}</Body1> : undefined}
          <Caption1 className={own.zeilensatz}>{assignmentSentence(rolle.assignmentCount)}</Caption1>
          {approvalNote(rolle, ohneRecht) !== undefined ? <Caption1 className={own.zweitzeile}>{approvalNote(rolle, ohneRecht)}</Caption1> : undefined}
          {renamePending(rolle) && rolle.approvedName !== null ? (
            <Caption1 className={own.zweitzeile}>{pendingRenameNote(rolle.approvedName)}</Caption1>
          ) : undefined}
        </div>
        {mitAktionen ? aktionen(rolle) : undefined}
        {rueckfrage === undefined ? undefined : <div className={own.voll}>{rueckfrage}</div>}
      </li>
    );
  };
  // Was auf die Lesende wartet, steht oben: fremde Vorschlaege, dann eigene, dann die freigegebenen
  // (DESIGN zu B3); innerhalb bleibt die Reihenfolge des Servers (sort ist stabil).
  const laufend = rollen.filter((rolle) => rolle.typeStatus !== 'ended').sort((a, b) => zeilenRang(a) - zeilenRang(b));
  const beendet = rollen.filter((rolle) => rolle.typeStatus === 'ended');

  return (
    <Card className={styles.card}>
      {/* Erklaersatz und Hauptaktion gehoeren zur Liste; ueber einer Stoerung stehen sie nicht. */}
      {status === 'geladen' ? (
        <div className={own.kopf}>
          <Body1 className={own.satz}>
            {CATALOG_NOTE}
            {ohneRecht ? ` ${STEWARD_CHANGE_NOTE}` : ''}
          </Body1>
          <AnlegenOeffner steuerung={anlegen} label="Rolle vorschlagen" disabled={busy} zuruecktreten={false} />
        </div>
      ) : undefined}
      <div aria-live="polite" role="status" aria-label="Status: Rollenkatalog" id={STATUS_REGION_ID} ref={statusRef} tabIndex={-1} className={own.hinweis}>
        {status === 'laedt' ? <Spinner labelPosition="after" label="Rollenkatalog wird geladen" /> : undefined}
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <div>{bestaetigung}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {fehler !== undefined ? <Meldung message={fehler} /> : undefined}
        {geschlossen !== undefined ? (
          <MessageBar intent="info">
            <MessageBarBody>
              <div>{geschlossen}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {ladefehler !== undefined ? <Meldung message={ladefehler} /> : undefined}
      </div>
      <VorgangNach meldungen={[fehler, ladefehler]} />
      {ladefehler?.retryable === true ? (
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}
      {status === 'geladen' ? (
        <>
          <AnlegenFormular
            steuerung={anlegen}
            name="Rolle vorschlagen"
            className={own.formular}
            onSenden={() => void vorschlagen()}
            absenden={
              <Button type="submit" appearance="primary" disabled={busy}>
                Vorschlagen
              </Button>
            }
          >
            <Field className={own.feld} label="Name" required validationMessage={feldfehler.name ?? null} hint="Mit Doppelpunkt, zum Beispiel „Übersetzer:in“.">
              <Input
                className={own.eingabe}
                id={feldId('name')}
                disabled={busy}
                value={eingabe.name}
                onChange={(_event, data) => {
                  setEingabe((alt) => ({ ...alt, name: hoechstens(data.value, MAXIMUM_ROLE_NAME_LENGTH) }));
                  // Wer korrigiert, sieht die alte Meldung am Feld nicht mehr.
                  setFeldfehler((alt) => (alt.name === undefined ? alt : Object.fromEntries(Object.entries(alt).filter(([feld]) => feld !== 'name'))));
                }}
              />
            </Field>
            <Field
              className={own.feld}
              label="Beschreibung"
              validationMessage={feldfehler.description ?? null}
              // Der Zaehler steht im Hinweis: unter dem Feld, am Feld beschrieben, ohne Live-Ansage je Taste.
              hint={
                <>
                  <span className={own.zweitzeile}>Was tut jemand mit dieser Rolle für einen Verlag, eine Holding oder einen Vertrieb? Freiwillig.</span>
                  <span className={own.zweitzeile}>{zeichenSatz(eingabe.description, MAXIMUM_ROLE_DESCRIPTION_LENGTH)}</span>
                </>
              }
            >
              <Textarea
                className={own.beschreibung}
                rows={3}
                resize="vertical"
                id={feldId('description')}
                disabled={busy}
                value={eingabe.description}
                onChange={(_event, data) => setEingabe((alt) => ({ ...alt, description: hoechstens(data.value, MAXIMUM_ROLE_DESCRIPTION_LENGTH) }))}
              />
            </Field>
          </AnlegenFormular>
          <div className={own.abschnitt}>
            <ul className={own.liste} aria-label="Rollen">
              {laufend.map((rolle) => zeile(rolle, true))}
            </ul>
            <div className={own.fuss}>
              <span>{laufend.length === 1 ? '1 Rolle' : `${laufend.length} Rollen`}{cursor !== undefined ? ' · weitere vorhanden' : ''}</span>
              {/* Im Baum ab dem ersten Bild, sonst ginge die erste Ansage verloren. Der innere Knoten
                  entsteht je Nachladen neu: ein gleicher Satz ("3 weitere …") wuerde sonst nicht erneut angesagt. */}
              <span ref={nachgeladenRef} tabIndex={-1} role="status" aria-label="Status: Weitere Rollen" aria-live="polite">
                {nachgeladen === undefined ? undefined : <span key={nachgeladen.mal}>{nachgeladen.satz}</span>}
              </span>
              {cursor !== undefined ? (
                <Button appearance="secondary" disabledFocusable={weitereLaedt} onClick={() => void weitereLaden()}>
                  {weitereLaedt ? 'Wird geladen …' : 'Weitere laden'}
                </Button>
              ) : undefined}
            </div>
          </div>
          {/* Beendete Rollen bleiben nachlesbar, stehen aber nicht zwischen den vergebbaren (DESIGN N5). */}
          {beendet.length > 0 ? (
            <Accordion collapsible className={own.abschnitt}>
              <AccordionItem value="beendet">
                {/* Beendete stehen zuletzt; solange weitere Seiten fehlen, ist die Zahl eine Untergrenze.
                    "+" nur sichtbar, vorgelesen "N oder mehr" (Glossar, Muster Ansprechpartner:innen). */}
                <AccordionHeader button={cursor === undefined ? {} : { 'aria-label': `Beendete Rollen (${beendet.length} oder mehr)` }}>
                  {`Beendete Rollen (${beendet.length}${cursor !== undefined ? '+' : ''})`}
                </AccordionHeader>
                <AccordionPanel>
                  <ul className={own.liste} aria-label="Beendete Rollen">
                    {beendet.map((rolle) => zeile(rolle, false))}
                  </ul>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          ) : undefined}
        </>
      ) : undefined}
    </Card>
  );
}
